import { createSession, GetMeResponse } from '@/actions/auth';
import { authenticatedFetch } from '@/lib/autheticatedFetch';
import { CreditCard, Customer, User } from '@/types/Auth';
import { deleteCookie } from 'cookies-next';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

export interface UserContextProps {
  user: User;
  customer: Customer;
  setUser: Dispatch<SetStateAction<User>>;
  isLogged: boolean;
  setIsLogged: Dispatch<SetStateAction<boolean>>;
  userGrant: string;
  setLoggedCustomer: (loggedCustomer: Customer) => void;
  getCreditCards: () => Promise<CreditCard[]>;
  setUserGrant: Dispatch<SetStateAction<string>>;
  logout(): Promise<void>;
  isLoaded: boolean;
  isDefaultUser: boolean;
}

interface UserProviderProps {
  children: ReactNode;
  getMeResponse: GetMeResponse;
}

export const UserContext = createContext<UserContextProps>(
  {} as UserContextProps,
);

const defaultUser = {
  id: 0,
  username: '',
  emailCanonical: '',
  enabled: false,
  user_grant: 'none',
  user_type: null,
  fullAccess: false,
  firstName: '',
  lastName: '',
  timestampAcceptedTerms: null,
  languageCode: 'en' as 'en' | 'it' | 'fr' | 'de',
};

export const UserContextProvider: FC<UserProviderProps> = ({
  children,
  getMeResponse,
}) => {
  const sessionIsValid = getMeResponse.ok && getMeResponse.data.data.session;
  const session = sessionIsValid ? getMeResponse.data.data.session : null;

  const [user, setUser] = useState<User>(session ?? defaultUser);
  const [customer, setCustomer] = useState<Customer>({} as Customer);
  const [isLogged, setIsLogged] = useState(!!session);
  const [userGrant, setUserGrant] = useState<string>(
    session?.user_grant ?? defaultUser.user_grant,
  );

  const isLoaded = true;

  async function logout() {
    setUser(defaultUser);
    setIsLogged(false);
    deleteCookie('PHPSESSID');
    deleteCookie('access_token');
    // Was surviving logout: it is only ever written, never read, so nothing
    // depended on it staying - but it is the credential that mints new access
    // tokens, and it has no business outliving the session on a shared machine.
    deleteCookie('refresh_token');
    deleteCookie('sessionId');
    // `session` no longer exists after login moved to next-admin; the delete
    // stays to clear it from a browser that logged in before the change.
    deleteCookie('session');

    await createSession();
    setCustomer({} as Customer);
    setUserGrant(defaultUser.user_grant);
    window.localStorage.removeItem('flagsmith-identity');
  }

  function setLoggedCustomer(loggedCustomer: Customer) {
    setCustomer(loggedCustomer);
  }

  async function getCreditCards() {
    if (isLogged) {
      const creditCards = await authenticatedFetch<CreditCard[]>({
        url: `/payment/methods`,
        method: 'GET',
      });

      return creditCards.data;
    }

    return [];
  }

  // real users can't have id === 0
  const isDefaultUser = useMemo(() => user?.id === 0, [user]);

  return (
    <UserContext.Provider
      value={{
        user,
        setLoggedCustomer,
        customer,
        getCreditCards,
        setUser,
        isLogged,
        setIsLogged,
        userGrant,
        setUserGrant,
        logout,
        isLoaded,
        isDefaultUser,
      }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
